import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, input, OnChanges, OnInit, Optional, output, PLATFORM_ID, Renderer2, signal, SimpleChanges } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, filter, share, startWith, distinctUntilChanged, map } from 'rxjs';
import { FormControl } from '@angular/forms';
import { AcvisService } from '@app/services/acvis.service';
import { MEDIA_TOKEN } from '../../../../../../tockens';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { MediaService } from '@app/services/media.service';

interface Item {
    id: string;
    code?: string;
    name: string;
    vc?: string;
    selected?: boolean;
}

@Component({
    selector: 'clever-searchlist',
    templateUrl: './searchlist.component.html',
    styleUrls: ['./searchlist.component.scss'],
    standalone: false
})
export class SearchlistComponent implements OnInit, OnChanges {
    mode = input<string>('');
    title = input<string>('');
    list = input<any[]>([]);

    listpriority = input<any[]>([]);

    showVc = input<boolean>(false);

    sendSelection = output<any>();

    isBrowser = signal<boolean>(false);
    showModal = signal<boolean>(false);
    focus = signal<boolean>(false);
    zielText = signal<string>('Beliebig');

    searchObj: Observable<any>;
    mySearchControl = new FormControl();
    selectedList = signal<Item[]>([]);
    private listChange$ = new BehaviorSubject<void>(undefined);

    overlaypos: ConnectedPosition[] = [
        {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
        }
    ];

    constructor(
        protected mediaService: MediaService,
        protected acvisService: AcvisService,
        protected render: Renderer2,
        @Inject(PLATFORM_ID) protected platformId: any,
        @Optional() @Inject(MEDIA_TOKEN) private media_token: string
    ) {
        this.isBrowser.set(isPlatformBrowser(this.platformId));
    }

    ngOnInit(): void {
        this.zielText.set(this.title());
        this.initSearchChange();
        this.checkSelectedList();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['list']) {
            this.checkSelectedList();
            this.listChange$.next();
        }

        if (changes['listpriority']) {
            this.listChange$.next();
        }
    }

    checkSelectedList(): void {
        const selectedItems: Item[] = [];
        if (this.list()) {
            for (let i = 0; i < this.list().length; i++) {
                if (this.list()[i].selected) {
                    selectedItems.push({
                        name: this.list()[i].name,
                        id: this.list()[i].id,
                        code: this.list()[i].id,
                    });
                }
            }
        }

        this.selectedList.set(selectedItems);
        if (this.selectedList()?.length > 0) {
            this.zielText.set(this.selectedList().length + ' ' + this.title());
        } else {
            this.zielText.set('Beliebig');
        }
    }

    initSearchChange(): void {
        const filterShare = this.mySearchControl.valueChanges.pipe(
            startWith(''),
            filter(value => (typeof value === 'string')),
            share()
        );

        // The list arrives async from the parent, so refresh results on both typing and input changes.
        this.searchObj = combineLatest([
            filterShare.pipe(distinctUntilChanged(), startWith('')),
            this.listChange$
        ]).pipe(
            map(([value]) => this.filterInList(value, this.list()))
        );
    }

    filterInList(value: string, array: any[]): any[] {
        const filterValue = String(value || '').trim().toLowerCase();
        const list = Array.isArray(array) ? array : [];
        if (filterValue?.length > 0) {
            return list.filter((option) => {
                return this.getSearchText(option).includes(filterValue);
            }).slice(0, 50);
        } else {
            return this.listpriority()?.length > 0 ? this.listpriority() : list.slice(0, 50);
        }
    }

    private getSearchText(option: any): string {
        return [
            option?.name,
            option?.code,
            option?.id,
            option?.vc,
            option?.land
        ].filter(Boolean).join(' ').toLowerCase();
    }

    async openModal() {
        this.showModal.set(true);
        this.focus.set(true);
    }

    async openModalMobile() {
        this.showModal.set(true);
        this.focus.set(true);
    }

    async closeModal() {
        this.showModal.set(false);
        this.mySearchControl.setValue('');
    }

    async closeModalMobile() {
        this.take();
    }

    clearText(): void {
        this.mySearchControl.setValue('');
        this.focus.set(false);

        setTimeout(() => {
            this.focus.set(true);
        }, 50);
    }

    selectValue(item): void {
        this.selectedList.set([...this.selectedList(), {
            name: item.name,
            id: item.id,
            code: item.id,
        }]);
        this.zielText.set(this.selectedList().length + ' ' + this.title());
    }

    isNotInlist(item): boolean {
        for (let i = 0; i < this.selectedList().length; i++) {
            if (this.selectedList()[i].id === item?.id) {
                return false;
            }
        }
        return true;
    }

    removeAllSelected(): void {
        this.selectedList.set([]);
        this.zielText.set('Beliebig');
    }

    removeFromSelected(k): void {
        this.selectedList.set(this.selectedList().filter((_, index) => index !== k));
        this.zielText.set(this.selectedList().length > 0 ? this.selectedList().length + '' : this.title());
    }

    take(): void {
        this.sendSelection.emit(JSON.parse(JSON.stringify(this.selectedList())));
        this.closeModal();
    }
}
